import * as babylon from "@babylonjs/core";

import { interpolateGradientColor } from "../../assetBuilders/vectorField";
import { BabylonState } from "../../state";

import { logTimeBinarySearch } from "./setPositionsToTime";

/** Reused as the origin point for the current-vector mesh segment each frame. */
const VECTOR_ORIGIN = Object.freeze(babylon.Vector3.Zero());

/**
 * Syncs each entity's vector field to the current time.
 *
 * Interpolates the current vector from the vector field cache and updates
 * the currentMesh geometry and gradient color.
 */
export function updateVectorFields(state: BabylonState) {
  const time = state.sceneConfig.time;
  if (time === undefined) {
    return;
  }
  for (const entity of Object.values(state.entities)) {
    if (
      !entity.vectorField ||
      entity.vectorField.cache.load.state !== "loaded" ||
      entity.vectorField.cache.logtimes.length === 0
    ) {
      continue;
    }

    const vfCache = entity.vectorField.cache;
    const vfIndex = logTimeBinarySearch(vfCache.logtimes, time);
    const lastVec = vfCache.allVectors[vfIndex];
    if (lastVec === undefined) {
      continue;
    }

    const nextVec = vfCache.allVectors[vfIndex + 1];
    const nextVecTime = vfCache.logtimes[vfIndex + 1];
    const lastVecTime = vfCache.logtimes[vfIndex];
    if (
      nextVec !== undefined &&
      nextVecTime !== undefined &&
      lastVecTime !== undefined &&
      nextVecTime !== lastVecTime
    ) {
      const lerpAmount = Math.min(
        Math.max(
          Number(time - lastVecTime) / Number(nextVecTime - lastVecTime),
          0,
        ),
        1,
      );
      entity.vectorField.current = babylon.Vector3.Lerp(
        lastVec,
        nextVec,
        lerpAmount,
      );
    } else {
      entity.vectorField.current = lastVec.clone();
    }

    // Update the currentMesh geometry to reflect the new vector.
    if (entity.vectorField.currentMesh) {
      const segment = [VECTOR_ORIGIN, entity.vectorField.current];
      entity.vectorField.currentMesh.setPoints(segment);

      // Interpolate color based on gradient when enabled.
      const gradient = entity.vectorField.gradient;
      if (gradient) {
        const mag = entity.vectorField.current.length();
        if (Number.isFinite(mag)) {
          const material = entity.vectorField.currentMesh.greasedLineMaterial;
          if (material) {
            material.color = interpolateGradientColor(gradient, mag);
          }
        }
      }
    }
  }
}
